import _extends from '@babel/runtime/helpers/extends';
import React__default, { useEffect, useCallback, useContext } from 'react';

const DialogsContext = /*#__PURE__*/React__default.createContext({});
const useDialogsContext = () => useContext(DialogsContext);
const useDialogsBlocker = (fn, deps = []) => {
  const {
    blocker
  } = useDialogsContext();
  useEffect(() => {
    blocker.current = fn;
    return () => {
      blocker.current = undefined;
    };
  }, deps);
};
const findDialog = (dialogs, Component) => dialogs.find(dialog => dialog.Component === Component);

/**
 * Returns function for opening dialog.
 * Dialog component must accept following props: isOpen, onClose, onClosed
 */
const useDialogsOpener = () => {
  const {
    blocker,
    dialogs,
    dispatch,
    nextId
  } = useDialogsContext();
  const open = (Component, props) => {
    const currentId = nextId.current ?? 0;
    const id = `dialog-${currentId}`;
    nextId.current = currentId + 1;
    dispatch({
      type: 'open',
      payload: {
        id,
        Component,
        props
      }
    });
    return id;
  };
  const updateOrOpen = (Component, props) => {
    const existingDialog = findDialog(dialogs, Component);
    if (!existingDialog?.isOpen) return open(Component, props);
    const {
      id
    } = existingDialog;
    dispatch({
      type: 'update',
      payload: {
        id,
        props
      }
    });
    return id;
  };
  return useCallback(async (Component, props = {}, openNew = false) => {
    if ((await blocker.current?.(Component, props)) === false) return null;
    const id = openNew ? open(Component, props) : updateOrOpen(Component, props);
    const close = () => {
      dispatch({
        type: 'close',
        payload: id
      });
    };
    const update = newProps => {
      dispatch({
        type: 'update',
        payload: {
          id,
          props: newProps
        }
      });
    };
    return {
      close,
      update
    };
  }, [dialogs]);
};
const useDialog = (Component, initialProps = {}) => {
  const openDialog = useDialogsOpener();
  const {
    dialogs,
    dispatch
  } = useDialogsContext();
  const open = useCallback(async (dialogProps = {}, openNew = false) => openDialog(Component, {
    ...initialProps,
    ...dialogProps
  }, openNew), [openDialog, Component, ...Object.values(initialProps)]);
  const update = useCallback(newProps => {
    const id = findDialog(dialogs, Component)?.id;
    if (id) dispatch({
      type: 'update',
      payload: {
        id,
        props: newProps
      }
    });
  }, [Component, dialogs]);
  const close = useCallback(() => {
    const id = findDialog(dialogs, Component)?.id;
    if (id) dispatch({
      type: 'close',
      payload: id
    });
  }, [Component, dialogs]);
  return [open, close, update];
};
const withDialogsOpener = Comp => componentProps => {
  const openDialog = useDialogsOpener();
  return /*#__PURE__*/React__default.createElement(Comp, _extends({}, componentProps, {
    openDialog: openDialog
  }));
};
const useDialogs = () => {
  const {
    dialogs,
    dispatch
  } = useDialogsContext();
  const open = useDialogsOpener();
  const isOpen = useCallback(Component => !!findDialog(dialogs, Component)?.isOpen, [dialogs]);
  const update = useCallback((Component, newProps) => {
    const id = findDialog(dialogs, Component)?.id;
    if (id) dispatch({
      type: 'update',
      payload: {
        id,
        props: newProps
      }
    });
  }, [dialogs]);
  const close = useCallback(Component => {
    const id = findDialog(dialogs, Component)?.id;
    if (id) dispatch({
      type: 'close',
      payload: id
    });
  }, [dialogs]);
  const clearAll = useCallback(() => dispatch({
    type: 'clearAll'
  }), []);
  return {
    dialogs,
    dispatch,
    isOpen,
    open,
    update,
    close,
    clearAll
  };
};
const withDialogs = Comp => componentProps => {
  const value = useDialogs();
  return /*#__PURE__*/React__default.createElement(Comp, _extends({}, componentProps, {
    nuiDialogs: value
  }));
};

export { DialogsContext, useDialog, useDialogs, useDialogsBlocker, useDialogsContext, useDialogsOpener, withDialogs, withDialogsOpener };
